/***************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2020 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/
/* global UXP_DEBUG */
import React from 'react';
import ReactDOM from 'react-dom';
import { VulcanHandler } from '@ccx-comments/wrapper';
import IsomorphicMessageBridge from '../isomorphicMessageBridge';

const createJsonString = jsonObj => {
  let result;
  try {
    result = JSON.stringify(jsonObj);
  } catch (err) {
    result = '{}';
  }
  return result;
};

const parseJsonString = jsonStr => {
  let result;
  try {
    result = JSON.parse(jsonStr);
  } catch (err) {
    result = {};
  }
  return result;
};

/**
 * Host class with overrides for External UXP Webview
 */
class ExternalHost {
  /**
   * Constructor
   * @param {HTMLElement} element - HTML Element in DOM to render app to.
   */
  constructor(element) {
    const { send, subscribe } = IsomorphicMessageBridge;

    /* FETCHERS */
    const handleFetchAssetMetadata = msgGuid =>
      this.fetchAssetMetadata().then(resp => {
        this._vulcanHandler.assetId = resp.id;
        send('setAssetMetadata', resp, msgGuid, false);
      });
    const handleFetchConfiguration = msgGuid =>
      this.fetchConfiguration().then(resp => send('setConfiguration', resp, msgGuid, false));
    const handleFetchImsData = msgGuid => this.fetchImsData().then(resp => send('setIMSData', resp, msgGuid, false));
    const handleFetchTheme = msgGuid => this.fetchTheme().then(resp => send('setTheme', resp, msgGuid, false));

    subscribe('getAssetMetadata', msgGuid => handleFetchAssetMetadata(msgGuid));
    this.mount.addEventListener('documentChanged', handleFetchAssetMetadata);
    this.mount.addEventListener('documentVersionChanged', handleFetchAssetMetadata);

    subscribe('getConfiguration', msgGuid => handleFetchConfiguration(msgGuid));
    subscribe('getIMSData', msgGuid => handleFetchImsData(msgGuid));

    subscribe('getTheme', msgGuid => handleFetchTheme(msgGuid));
    // eslint-disable-next-line no-unused-expressions
    window.require('uxp').host.addEventListener?.('themechanged', handleFetchTheme);

    /* HANDLE COMPONENT EVENTS */
    subscribe('onAnnotationToggle', (msgGuid, payload) =>
      this.mount.onAnnotationToggle(this.processOutgoingPayload(payload))
    );
    subscribe('onAnnotatingChange', (msgGuid, payload) =>
      this.mount.onAnnotatingChange(this.processOutgoingPayload(payload))
    );
    subscribe('onCommentClick', (msgGuid, payload) => this.mount.onCommentClick(this.processOutgoingPayload(payload)));
    subscribe('onCommentHover', (msgGuid, payload) => this.mount.onCommentHover(this.processOutgoingPayload(payload)));
    subscribe('onCommentUnhover', (msgGuid, payload) =>
      this.mount.onCommentUnhover(this.processOutgoingPayload(payload))
    );
    subscribe('onCommentsUpdate', (msgGuid, payload) =>
      this.mount.onCommentsUpdate(this.processOutgoingPayload(payload))
    );
    subscribe('onCommentsSuccess', (msgGuid, payload) =>
      this.mount.onCommentsSuccess(this.processOutgoingPayload(payload))
    );
    subscribe('onNodeChange', (msgGuid, payload) => this.mount.onNodeChange(this.processOutgoingPayload(payload)));
    subscribe('onShare', (msgGuid, payload) => this.mount.onShare(this.processOutgoingPayload(payload)));
    subscribe('onShowCommentComplete', (msgGuid, payload) =>
      this.mount.onShowCommentComplete(this.processOutgoingPayload(payload))
    );

    /* HANDLE HOST EVENTS */
    this.mount.addEventListener('batchCommentUpdate', this.handleBatchCommentUpdate.bind(this));
    this.mount.addEventListener('createPendingAnnotation', this.handleCreatePendingAnnotation.bind(this));
    this.mount.addEventListener('highlightComment', this.handleHighlightComment.bind(this));
    this.mount.addEventListener('reconfigureShareStatus', this.handleReconfigureShareStatus.bind(this));
    this.mount.addEventListener('remapComments', this.handleRemapComments.bind(this));
    this.mount.addEventListener('removePendingAnnotation', this.handleRemovePendingAnnotation.bind(this));
    this.mount.addEventListener('setAssetMetadata', this.handleSetAssetMetadata.bind(this));
    this.mount.addEventListener('setConfiguration', this.handleSetConfiguration.bind(this));
    this.mount.addEventListener('setIMS', this.handleSetIMS.bind(this));
    this.mount.addEventListener('setTheme', this.handleSetTheme.bind(this));
    this.mount.addEventListener('showComment', this.handleShowComment.bind(this));
    this.mount.addEventListener('unhighlightComment', this.handleUnhighlightComment.bind(this));

    this._vulcanHandler = new VulcanHandler(() => send('fetchComments'));

    this.element = element;
    this.stringifyJson = false;
    this.render();
  }

  /* eslint-disable class-methods-use-this */
  /**
   * Returns host API object
   * @returns {Object} hostApi
   */
  get mount() {
    return window.ccxCommentsHostApi;
  }

  /**
   * Parse JSON string if required
   * @param {String|Object} payload - host payload
   * @returns {Object} response
   */
  processIncomingPayload(payload) {
    let result = payload;

    if (this.stringifyJson) {
      result = parseJsonString(payload);
    }
    return result;
  }

  /**
   * Stringify JSON object if required
   * @param {Object} payload - outgoing JSON payload
   * @returns {String|Object} response
   */
  processOutgoingPayload(payload) {
    let result = payload;

    if (this.stringifyJson) {
      result = createJsonString(payload);
    }
    return result;
  }

  /**
   * Returns promise for fetching asset metadata
   * @returns {Promise} assetMetadata promise
   */
  fetchAssetMetadata() {
    return Promise.resolve(this.processIncomingPayload(this.mount.getAssetMetadata()));
  }

  /**
   * Returns configuration object from host
   * @param {Object} hostConfigOverride - config to use in place of requesting from host
   * @returns {Promise} assetMetadata promise
   */
  getHostConfig(hostConfigOverride) {
    return hostConfigOverride || this.processIncomingPayload(this.mount.getConfiguration());
  }

  /**
   * Returns promise for fetching configuration
   * @param {Object} hostConfigOverride - config to use in place of requesting from host
   * @returns {Promise} configuration promise
   */
  fetchConfiguration(hostConfigOverride) {
    return new Promise(resolve => {
      this.fetchUxpHostData().then(uxpHostData => {
        const hostConfig = this.getHostConfig(hostConfigOverride);

        if (hostConfig.debug !== this.debug) {
          this.debug = hostConfig.debug;
          this.render();
        }

        resolve({
          ...uxpHostData,
          ...hostConfig
        });
      });
    });
  }

  /**
   * Returns promise for fetching ims data
   * @returns {Promise} imsData promise
   */
  fetchImsData() {
    return Promise.resolve(this.processIncomingPayload(this.mount.getIMSData()));
  }

  /**
   * Returns promise for fetching theme
   * @returns {Promise} theme promise
   */
  fetchTheme() {
    return window.require('uxp').host.getTheme();
  }

  /**
   * Returns promise for returning generic UXP host information
   * @returns {Promise} UXP host data promise
   */
  fetchUxpHostData() {
    const uxpHostData = window.require('uxp').host;
    const configFromUxp = {
      appId: uxpHostData.appCode,
      appName: uxpHostData.name,
      appVersion: uxpHostData.version,
      device: 'desktop',
      locale: uxpHostData.locale.replace('_', '-'),
      platform: uxpHostData.platform
    };

    return new Promise(resolve => {
      uxpHostData.canLogAnalyticsData
        .then(canLogAnalytics => {
          resolve({
            ...configFromUxp,
            enableAnalytics: canLogAnalytics
          });
        })
        .catch(() => {
          resolve({
            ...configFromUxp,
            enableAnalytics: false
          });
        });
    });
  }

  /* HANDLE EVENTS FROM HOST */

  /**
   * Updates target object of specified comments.
   * @param {Object} payload - Key value pairs commentId: target object
   * @returns {undefined}
   */
  handleBatchCommentUpdate(payload) {
    const { batchUpdatePayload } = this.processIncomingPayload(payload);

    IsomorphicMessageBridge.send('batchCommentUpdate', batchUpdatePayload, undefined, false);
  }

  /**
   * Creates a pending annotation based on host coordinate data
   * @param {Object} payload - host defined annotation data
   * @returns {undefined}
   */
  handleCreatePendingAnnotation(payload) {
    const { annotationPayload } = this.processIncomingPayload(payload);

    IsomorphicMessageBridge.send('createPendingAnnotation', annotationPayload, undefined, false);
  }

  /**
   * Scrolls to and highlights the given comment.
   * @param {Object} payload - id of comment to highlight to
   * @returns {undefined}
   */
  handleHighlightComment(payload) {
    const { commentId } = this.processIncomingPayload(payload);

    IsomorphicMessageBridge.send('highlightComment', commentId, undefined, false);
  }

  /**
   * Refetches share information from Invitation service
   * @returns {undefined}
   */
  handleReconfigureShareStatus() {
    IsomorphicMessageBridge.send('reconfigureShareStatus', undefined, undefined, false);
  }

  /**
   * Updates node of specified comments.
   * @param {Object} payload - Key value pairs commentId: nodeId of which comments and where to map them to.
   * @returns {undefined}
   */
  handleRemapComments(payload) {
    const { remapObject } = this.processIncomingPayload(payload);

    IsomorphicMessageBridge.send('remapComments', remapObject, undefined, false);
  }

  /**
   * Removes a pending annotation
   * @returns {undefined}
   */
  handleRemovePendingAnnotation() {
    IsomorphicMessageBridge.send('removePendingAnnotation', undefined, undefined, false);
  }

  /**
   * Sets asset metadata.
   * @param {Object} payload - asset data
   * @returns {undefined}
   */
  handleSetAssetMetadata(payload) {
    const { assetMetadata } = this.processIncomingPayload(payload);

    this._vulcanHandler.assetId = assetMetadata.id;
    IsomorphicMessageBridge.send('setAssetMetadata', assetMetadata, undefined, false);
  }

  /**
   * Sets configuration metadata.
   * @param {Object} payload - configuration metadata
   * @returns {undefined}
   */
  handleSetConfiguration(payload) {
    const { configuration } = this.processIncomingPayload(payload);

    this.fetchConfiguration(configuration).then(newConfig => {
      IsomorphicMessageBridge.send('setConfiguration', newConfig, undefined, false);
    });
  }

  /**
   * Updates the commenting panel when the IMS data changes.
   * @param {Object} payload - ims data object
   * @returns {undefined}
   */
  handleSetIMS(payload) {
    const { imsData } = this.processIncomingPayload(payload);

    IsomorphicMessageBridge.send('setIMS', imsData, undefined, false);
  }

  /**
   * Updates the commenting panel when the active theme changes.
   * @param {Object} payload - new active theme.
   * @returns {undefined}
   */
  handleSetTheme(payload) {
    const { theme } = this.processIncomingPayload(payload);

    IsomorphicMessageBridge.send('setTheme', theme, undefined, false);
  }

  /**
   * Scrolls to and highlights the given comment.
   * @param {Object} payload - Id of the comment to scroll to.
   * @returns {undefined}
   */
  handleShowComment(payload) {
    const { commentId } = this.processIncomingPayload(payload);

    IsomorphicMessageBridge.send('showComment', commentId, undefined, false);
  }

  /**
   * Removes highlight from currently highlighted comment.
   * @returns {undefined}
   */
  handleUnhighlightComment() {
    IsomorphicMessageBridge.send('unhighlightComment', undefined, undefined, false);
  }
  /* eslint-enable class-methods-use-this */

  /**
   * Render the webview
   * @returns {undefined}
   */
  render() {
    const uxpAllowInspector = typeof UXP_DEBUG !== 'undefined' || !!this.debug;

    ReactDOM.render(
      <webview id="commentsWebview" src="internal.html" uxpAllowInspector={String(uxpAllowInspector)} />,
      this.element
    );
  }
}

export default ExternalHost;
